'use client';

import { latestNewsGridSection } from '@stereoagency/sanity-schema';
import { ThumbnailListEntriesSection } from '@stereoagency/ui';
import flush from 'just-flush';
import useTranslation from 'next-translate/useTranslation';
import { defaultLanguage } from '@stereoagency/constants';
import {
  actionAsProps,
  createDocumentURL,
  findInKeyArray,
  sanityImageAsProps
} from '@/utils';
import { newsQuery } from './newsQuery';
import { SimpleBlockContent } from '@/components/sanity/client';

type Props = Omit<
  typeof latestNewsGridSection.schema.latestNewsGridSection,
  '_type'
> & {
  articles: (typeof newsQuery)[1];
};

export default function LatestNewsGridSection({
  action,
  title,
  articles
}: Props) {
  const { lang } = useTranslation();

  if (articles.length === 0) {
    return null;
  }

  return (
    <ThumbnailListEntriesSection
      title={title}
      action={action ? actionAsProps(action) : undefined}
      entries={flush(
        articles.map((article) => {
          const i18ntitle = article.title
            ? findInKeyArray(article.title, [lang, defaultLanguage.id])
            : null;
          const i18nintro = article.introduction
            ? findInKeyArray(article.introduction, [lang, defaultLanguage.id])
            : null;

          if (
            !article.slug?.current ||
            !i18ntitle?.value ||
            !article.thumbnail?.asset._ref
          ) {
            return null;
          }

          return {
            variant: 'inverted',
            image: sanityImageAsProps(article.thumbnail),
            title: i18ntitle.value,
            description: i18nintro?.value ? (
              <SimpleBlockContent value={i18nintro.value} />
            ) : null,
            action: {
              href: createDocumentURL(lang, 'article', article.slug.current)
            }
          };
        })
      )}
    />
  );
}
